import { Button, CircularProgress, IconButton, Typography } from "@mui/material";
import { Check, Close } from "@mui/icons-material";
import { TreeView, TreeItem } from '@mui/x-tree-view';
import clsx from "clsx";
import { Formik } from "formik";
import _ from "lodash";
import { tz } from "moment-timezone";
import React, { useEffect, useMemo, useState } from "react";
import { t } from "ttag";
import * as Yup from "yup";
import Checkbox from "../../cool_widgets/CoolCheckbox";
import { EditIcon } from "../../iconImages";
import { ArrowDownO, CheckboxChecked } from "../../icons";
import { useStoreActions, useStoreState } from "../../models/RootStore";
import AdvancedInputField from "../../widgets/AdvancedInputField/AdvancedInputField";
import ServiceNavigationBar from "../../widgets/Menu/ServiceNavigationBar";
import { countries } from "./countryList";
import { applyActivePeriod, initialActivePeriod } from "./siteInvoicingFields";
import useStyles from "./SiteManagement.style";
import { stateList } from "./stateList";
import { debugPackage, trialPackage } from "./temporaryPackageField";

const timeZones = tz.names().map((timezone) => ({
  value: timezone,
  label: `${timezone}  ${tz(timezone).format("Z")}`
}));
const stateOptions = stateList.map((state: any) => ({
  value: state.value,
  label: state.label
}));
const countryOptions = countries.map((country: any) => ({
  value: country.code,
  label: country.name
}));

const packageTypes: any = {
  "Professional Packages": "nested"
};

const initMainPackageFlags: any = {
  "Professional Packages": false
};

const SiteDetails = (props: any) => {
  const styles: any = useStyles();
  const [site, setSite] = useState<any>({});
  const [editable, setEditable] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [mainPackageFlags, setMainPackageFlags] = useState<any>(initMainPackageFlags);
  const { sitePackagesNamesEnums = {}, sitePackagesFlagsEnums = {} } = useStoreState((state) => state.types);
  const commercialPackageEnumMapped: any = Object.keys(sitePackagesFlagsEnums).reduce((acc: any, cur: string) => {
    if (sitePackagesFlagsEnums[+cur] === "hvacControl" || +cur === 1) { return acc }
    acc[sitePackagesFlagsEnums[+cur]] = sitePackagesNamesEnums[+cur];
    return acc;
  }, {});

  // Only the checked state lives here. Labels are read from the types enums at
  // render time, since those may still be loading on the first render.
  const initCommercialPackage: any = {
    hvacAdvancedOperations: { "value": false },
    predictiveMaintenance: { "value": false },
  }
  const [commercialPackageFlags, setCommercialPackageFlags] = useState<any>({ ...initCommercialPackage });
  const [debugPackageEnabled, setDebugPackageEnabled] = useState<boolean>(false);
  const [trialPackageEnabled, setTrialPackageEnabled] = useState<boolean>(false);
  const getSite = useStoreActions((actions) => actions.sites.getSite);
  const updateSite = useStoreActions((actions) => actions.sites.updateSite);
  const { addMessage } = useStoreActions((action) => action.errorMessage);
  const setSelections = useStoreActions((a) => a.selections.setSelections);
  const { mySupportedPages = "all" } = useStoreState((s) => s.users);

  const {
    siteCategories = {},
    currencyTypes = {},
    sitePhases = {},
    siteOnboardingStatus = {},
    sellingEntities = {}
  } = useStoreState((s) => s.types);

  const showAllPages = mySupportedPages === "all";

  const onboardingStatuses = Object.entries(siteOnboardingStatus).map(([label, value]) => ({ value, label }));
  const currencies = Object.entries(currencyTypes).map(([label, value]) => ({ value, label }));
  const sitePhasesOptions = Object.entries(sitePhases).map(([label, value]) => ({ value, label }));
  const categories = Object.entries(siteCategories).map(([label, value]) => ({ value, label }));
  const sellingEntityOptions = Object.entries(sellingEntities).map(([label, value]) => ({ value, label }));
  // Held for the life of the loaded site: applyActivePeriod compares the
  // submitted values against exactly what the pickers started with.
  const activePeriodInitial = useMemo(() => initialActivePeriod(site), [site]);
  // Track the initial debug package expiration
  const debugPackageExpiresAtInitial = useMemo(() => debugPackage.initialExpiresAt(site), [site]);
  // Track the initial trial package expiration
  const trialPackageExpiresAtInitial = useMemo(() => trialPackage.initialExpiresAt(site), [site]);
  // Tracks whether each package was already active on load.
  const debugPackageInitiallyEnabled = useMemo(() => debugPackage.isActive(site), [site]);
  const trialPackageInitiallyEnabled = useMemo(() => trialPackage.isActive(site), [site]);

  useEffect(() => {
    if (_.isEmpty(site)) {
      return;
    }
    setInitialflags(site);
  }, [site]);

  const setInitialflags = (site: any) => {
    const packageKeys = Object.keys(commercialPackageFlags || {});
    const nextCommercialPackageFlags = packageKeys.reduce((acc: Record<string, { value: boolean }>, key: string) => {
      acc[key] = { ...commercialPackageFlags[key], value: !!site[key] };
      return acc;
    }, {});

    setMainPackageFlags({
      ...mainPackageFlags,
      "Professional Packages": packageKeys.every((key) => key === "hvacControl" || !!site[key])
    });
    setCommercialPackageFlags(nextCommercialPackageFlags);
    setDebugPackageEnabled(debugPackage.isActive(site));
    setTrialPackageEnabled(trialPackage.isActive(site));
  };

  const { match, history } = props;
  const { params } = match;
  const { siteId } = params;
  const {
    name,
    description,
    country,
    city,
    address,
    postalCode,
    state,
    timezone,
    customer,
    category,
    onBoardingStatus,
    ////////////////////////
    phase,
    connectedIDUs,
    creatingDate,
    // devices,
    numberOfDevices,
    financeContact,
    invoiceNum,
    PONum,
    sfOrderNum,
    contractedIDUs,
    pricePerIDU,
    CRMReferenceID,
    sellingEntity,
    payingCustomerName,
    isEnforcePackage,
    customerName,
    customerSalesOwner,
    salesAccountEmail,
  } = site;
  // const numberOfDevices = devices?.length || 0;
  const validationSchema = Yup.object({
    name: Yup.string().required(t`required field`),
    country: Yup.string().required(t`required field`),
    city: Yup.string().required(t`required field`),
    address: Yup.string().required(t`required field`),
    timezone: Yup.string().required(t`required field`),
    salesAccountEmail: Yup.string().trim().email(t`Invalid email`)
  });

  useEffect(() => {
    (async () => {
      if (!_.isEmpty(site) || !siteId) {
        return;
      }

      getSite(siteId).then(async (data: any) => {
        setSite(data);
      });
    })();

  }, [getSite, site, siteId]);

  const handleSubmit = (values: any, { setSubmitting }: any) => {
    setError("");
    let commercialPackageValues: any = [];
    Object.keys(commercialPackageFlags).forEach((option: any) => commercialPackageValues[option] = commercialPackageFlags[option].value);
    
    const packagesObj = {
      ...commercialPackageValues
    };
    const renewalDate = values?.renewalDate ? new Date(values.renewalDate)?.getTime() : null;
    let siteData = {
      ...values,
      renewalDate,
      postalCode: values.zip,
      ...packagesObj
    };
    delete siteData.zip;
    delete siteData.creatingDate;
    delete siteData.numberOfDevices;
    delete siteData.customerName;
    delete siteData.customerSalesOwner;
    delete siteData.PO;
    !renewalDate && delete siteData.renewalDate;
    siteData.contractedIDUs === "" && delete siteData.contractedIDUs;
    siteData = applyActivePeriod(siteData, values, activePeriodInitial);
    siteData = debugPackage.apply(
      siteData,
      debugPackageEnabled,
      values.debugPackageExpiresAt,
      debugPackageExpiresAtInitial,
      debugPackageInitiallyEnabled
    );
    siteData = trialPackage.apply(
      siteData,
      trialPackageEnabled,
      values.trialPackageExpiresAt,
      trialPackageExpiresAtInitial,
      trialPackageInitiallyEnabled
    );

    updateSite({
      id: siteId,
      data: siteData
    })
      .then((updatedSite: any) => {
        // Merge the server response last so server-computed fields win, while
        // fields not returned by the API are preserved.
        setSite({ ...site, ...updatedSite });
        setEditable(false);
      })
      .catch((err: any) => {
        addMessage({ message: err.message });
      })
      .finally(() => setSubmitting(false));

  };

  const StyledTreeItem = (props: any) => {
    const { className, labelText, labelIcon: LabelIcon, node, labelInfo, color, bgColor, styles: itemStyles = {}, ...other } = props;
    return (
      <TreeItem
        onClick={(event: any) => { if (!editable) { event.preventDefault(); event.stopPropagation(); } }}
        disabled={!editable}
        label={
          <div className={clsx(styles.labelRoot, { [className]: className })}>
            {LabelIcon && <LabelIcon color="inherit" className={styles.labelIcon} />}
            {node && node}
            <Typography variant="body2" className={styles.labelText}>
              {labelText}
            </Typography>
            <Typography variant="caption" color="inherit">
              {labelInfo}
            </Typography>
          </div>
        }
        style={{
          "--tree-view-color": color,
          "--tree-view-bg-color": bgColor
        }}
        classes={{
          root: styles.root,
          content: !editable
            ? (itemStyles.disabledContent || styles.disabledContent)
            : (itemStyles.content || styles.content),
          expanded: styles.expanded,
          selected: styles.selected,
          group: itemStyles.group || styles.group,
          label: !editable
            ? (itemStyles.disabledLabel || styles.disabledLabel)
            : (itemStyles.label || styles.label),
          iconContainer: itemStyles.iconContainer || styles.iconContainer
        }}
        {...other}
      />
    );
  };

  const Checkbox1 = (props: any) => {
    return <Checkbox
      color="default"
      edge="end"
      variant="outlined"
      disabled={!editable}
      onChange={() => { }}
      onClick={(event: any) => event.stopPropagation()}
      checkedIcon={<CheckboxChecked style={{ width: 16, height: 16 }} />}
      className={clsx(styles.smallCheckbox, { [props.className]: props.className })}
      {...props}
    />;
  };

  const StyledItem = ({ option, onChange, subItem = false }: any) => {
    const mainFlag = packageTypes[option] === "nested" ? Object.values(commercialPackageFlags).every((option: any) => option.value) : mainPackageFlags[option];
    const checked = (subItem ? commercialPackageFlags[option].value : mainFlag) || false;
    const label = subItem ? commercialPackageEnumMapped[option] : option;
    const indeterminate = packageTypes[option] === "nested" ? Object.values(commercialPackageFlags).some((option: any) => option.value) : false;

    return (<StyledTreeItem
      key={option}
      nodeId={option}
      labelText={label}
      disabled={!editable}
      styles={subItem ? undefined : {
        iconContainer: styles.customerIconContainer,
        group: styles.customerGroup,
        label: styles.label,
        content: styles.customerContent,
        disabledContent: styles.customerDisabledContent
      }}
      color="#545964"
      bgColor="#fff"
      node={<Checkbox1
        indeterminate={!checked && indeterminate}
        checked={checked}
        onChange={onChange}
      />}
    >
      {packageTypes[option] === "nested" && (
        Object.keys(commercialPackageFlags).map((subItem: any) => {
          return <StyledItem key={subItem} option={subItem} subItem={true} onChange={(event: any) => {
            setCommercialPackageFlags({ ...commercialPackageFlags, [subItem]: { ...commercialPackageFlags[subItem], value: event.target.checked } });
          setMainPackageFlags({ ...mainPackageFlags});
          }} />;
        })
      )}
    </StyledTreeItem>);
  };

  return (
    <ServiceNavigationBar title={name} {...props}>
      <div className={styles.pageContainer}>

        <div className={styles.siteDetailsContainer}>
          <Formik
            initialValues={{
              id: siteId,
              CRMReferenceID,
              customerName,
              customerSalesOwner,
              salesAccountEmail: salesAccountEmail || "",
              name,
              description,
              country,
              city,
              address,
              zip: postalCode,
              timezone,
              state,
              category,
              onBoardingStatus,
              phase,
              connectedIDUs,
              creatingDate,
              numberOfDevices,
              financeContact,
              invoiceNum,
              PONum,
              sfOrderNum,
              contractedIDUs,
              pricePerIDU,
              sellingEntity,
              payingCustomerName,
              isEnforcePackage: !!isEnforcePackage,
              ...activePeriodInitial,
              debugPackageExpiresAt: debugPackageExpiresAtInitial,
              trialPackageExpiresAt: trialPackageExpiresAtInitial
            }}
            enableReinitialize={true}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {(props) => {
              const {
                values,
                touched,
                errors,
                handleChange,
                handleSubmit,
                handleReset,
                dirty,
                isSubmitting
              } = props;
              return (
                <form onSubmit={handleSubmit} className={styles.siteDetailsForm}>
                  <div className={styles.siteDetailsHeader}>
                    <Typography className={styles.editSiteTitle}>
                      {t`Site Details`}
                    </Typography>
                    <Typography className={styles.errorMessage}>{error}</Typography>
                    {isSubmitting && (
                      <CircularProgress className={styles.loader} />
                    )}
                    {!editable && (
                      <IconButton onClick={() => setEditable(true)}>
                        <img src={EditIcon} alt="edit" />
                      </IconButton>
                    )}
                    {editable && (
                      <div style={{ display: "flex" }}>
                        <IconButton type="submit" disabled={isSubmitting}>
                          <Check className={styles.siteDetailsIcons} />
                        </IconButton>
                        <IconButton
                          disabled={isSubmitting}
                          onClick={() => {
                            handleReset();
                            setInitialflags(site);
                            setEditable(false);
                          }}
                        >
                          <Close className={styles.siteDetailsIcons} />
                        </IconButton>
                      </div>
                    )}
                  </div>
                  <div className={styles.siteDetailsFieldsContainer}>
                    <AdvancedInputField
                        name="name"
                        label={t`Name`}
                        editable={editable}
                        {...props}
                    />
                    <AdvancedInputField
                        name="id"
                        label={t`ID`}
                        editable={false}
                        {...props}
                    />
                    <AdvancedInputField
                        name="customerName"
                        label={t`Customer Name`}
                        editable={false}
                        {...props}
                    />
                    {/* The customer's Sales account owner, changed from the customer page */}
                    <AdvancedInputField
                        name="customerSalesOwner"
                        label={t`Sales Account Owner`}
                        editable={false}
                        {...props}
                    />
                    {/* Shown to the site's admins in the subscription renewal notice */}
                    <AdvancedInputField
                        name="salesAccountEmail"
                        label={t`Sales Account Email`}
                        placeholder={t`name@coolautomation.com`}
                        type="email"
                        editable={editable}
                        {...props}
                    />
                    <AdvancedInputField
                        name="CRMReferenceID"
                        label={t`CRM Reference ID`}
                        editable={false}
                        {...props}
                    />
                    <AdvancedInputField
                      name="description"
                      label={t`Description`}
                      editable={editable}
                      {...props}
                    />
                    <AdvancedInputField
                      name="address"
                      label={t`Address`}
                      editable={editable}
                      {...props}
                    />
                    <AdvancedInputField
                      name="city"
                      label={t`City`}
                      editable={editable}
                      {...props}
                    />
                    <AdvancedInputField
                      select
                      value={values.state || ""}
                      name="state"
                      options={stateOptions}
                      label={t`State`}
                      editable={editable}
                      {...props}
                    />
                    <AdvancedInputField
                      name="zip"
                      label={t`Zip`}
                      editable={editable}
                      {...props}
                    />
                    <AdvancedInputField
                      select
                      value={values.country || ""}
                      name="country"
                      label={t`Country`}
                      options={countryOptions}
                      editable={editable}
                      {...props}
                    />

                    <AdvancedInputField
                      select
                      value={values.timezone}
                      name="timezone"
                      label={t`Timezone`}
                      options={timeZones}
                      editable={editable}
                      {...props}
                    />
                    <AdvancedInputField
                      select
                      value={values.category}
                      name="category"
                      label={t`Facility Type`}
                      options={categories}
                      editable={editable}
                      {...props}
                    />
                    <AdvancedInputField
                      select
                      value={values.onBoardingStatus}
                      name="onBoardingStatus"
                      label={t`onboarding status`}
                      options={onboardingStatuses}
                      editable={editable}
                      {...props}
                    />
                    <AdvancedInputField
                      select
                      value={values.phase}
                      name="phase"
                      label={t`Site Journey Phase`}
                      options={sitePhasesOptions}
                      editable={editable}
                      {...props}
                    />
                   
                    <AdvancedInputField
                      name="connectedIDUs"
                      label={t`IDU connected`}
                      editable={false}
                      type={"number"}
                      min={0}
                      {...props}
                    />
                    <AdvancedInputField
                      name="contractedIDUs"
                      label={t`Licensed IDUs`}
                      type={"number"}
                      min={0}
                      editable={editable}
                      {...props}
                    />
                    <AdvancedInputField
                      name="creatingDate"
                      label={t`Service start date`}
                      editable={false}
                      {...props}
                    />
                    <AdvancedInputField
                      name="numberOfDevices"
                      label={t`#Devices`}
                      type={"number"}
                      min={0}
                      editable={false}
                      {...props}
                    />                  
                   
                    <AdvancedInputField
                      name="PONum"
                      label={t`PO Number`}
                      editable={editable}
                      {...props}
                    />
                    <AdvancedInputField
                      name="sfOrderNum"
                      label={t`SF Order #`}
                      editable={editable}
                      {...props}
                    />
                    <AdvancedInputField
                      select
                      value={values.sellingEntity}
                      name="sellingEntity"
                      label={t`Selling Entity`}
                      options={sellingEntityOptions}
                      editable={editable}
                      {...props}
                    />
                    <AdvancedInputField
                      name="payingCustomerName"
                      label={t`Paying Customer Name`}
                      editable={editable}
                      {...props}
                    />
                    <AdvancedInputField
                      datePicker
                      name="activePeriodStart"
                      label={t`Active Period Start`}
                      placeholder={t`N/A`}
                      editable={editable}
                      {...props}
                    />
                    <AdvancedInputField
                      datePicker
                      name="activePeriodEnd"
                      label={t`Active Period End`}
                      placeholder={t`N/A`}
                      editable={editable}
                      {...props}
                    />
                    <AdvancedInputField
                      name="invoiceNum"
                      label={t`Invoice #`}
                      editable={editable}
                      {...props}
                    />

                    <AdvancedInputField
                      toggle
                      name="isEnforcePackage"
                      label={t`Enforce Site Package`}
                      editable={editable}
                      {...props}
                    />
                    <div className={styles.flagsTreeContainer}>
                      <TreeView
                        className={styles.root}
                        disableSelection={!editable}
                        defaultExpanded={["Professional Packages"]}
                        defaultCollapseIcon={<><div className={styles.arrowContainer} /> <ArrowDownO className={styles.arrow} /> </>}
                        defaultExpandIcon={<><div className={styles.arrowContainer} /> <ArrowDownO className={clsx(styles.arrow, styles.rotateArrow)} /></>}
                        defaultEndIcon={<div className={styles.arrowContainer} />}
                      >
                        {mainPackageFlags && Object.keys(mainPackageFlags).map((option: string) =>
                          <StyledItem key={option} option={option} onChange={(event: any) => {
                            const { target: { checked } } = event;
                            mainPackageFlags[option] = checked;
                            
                            const value = option === "Professional Packages" ? checked : false;

                            Object.keys(commercialPackageFlags).forEach((option: any) => commercialPackageFlags[option].value = value);
                            setCommercialPackageFlags({ ...commercialPackageFlags });
                            setMainPackageFlags({ ...mainPackageFlags });
                          }} />
                        )}
                      </TreeView>
                    </div>

                    <div className={styles.temporaryPackageContainer}>
                      <TreeView className={styles.root}>
                        <StyledTreeItem
                          key="trialPackage"
                          nodeId="trialPackage"
                          labelText={t`Customer Trial Period`}
                          disabled={!editable}
                          styles={{
                            iconContainer: styles.customerIconContainer,
                            group: styles.customerGroup,
                            label: styles.temporaryPackageLabel,
                            disabledLabel: styles.temporaryPackageDisabledLabel,
                            content: styles.temporaryPackageContent,
                            disabledContent: styles.temporaryPackageDisabledContent
                          }}
                          color="#545964"
                          bgColor="#fff"
                          node={<Checkbox1
                            checked={trialPackageEnabled}
                            onChange={(event: any) => setTrialPackageEnabled(event.target.checked)}
                          />}
                        />
                      </TreeView>

                      <Typography variant="body2" className={styles.temporaryPackageSubHeader}>
                        {t`Assign predictive maintenance package for the site, Applies for site users with professional app access`}
                      </Typography>

                      <AdvancedInputField
                        datePicker
                        boxedDatePicker
                        disablePast
                        hideBorder
                        containerClassName={styles.temporaryPackageExpiresAtContainer}
                        name="trialPackageExpiresAt"
                        label={t`Expires on`}
                        editable={editable}
                        hint={t`Default 30 days. Can be extended.`}
                        {...props}
                      />
                    </div>

                    <div className={styles.temporaryPackageContainer}>
                      <TreeView className={styles.root}>
                        <StyledTreeItem
                          key="debugPackage"
                          nodeId="debugPackage"
                          labelText={t`CA customer support debug`}
                          disabled={!editable}
                          styles={{
                            iconContainer: styles.customerIconContainer,
                            group: styles.customerGroup,
                            label: styles.temporaryPackageLabel,
                            disabledLabel: styles.temporaryPackageDisabledLabel,
                            content: styles.temporaryPackageContent,
                            disabledContent: styles.temporaryPackageDisabledContent
                          }}
                          color="#545964"
                          bgColor="#fff"
                          node={<Checkbox1
                            checked={debugPackageEnabled}
                            onChange={(event: any) => setDebugPackageEnabled(event.target.checked)}
                          />}
                        />
                      </TreeView>

                      <Typography variant="body2" className={styles.temporaryPackageSubHeader}>
                        {t`Assign predictive maintenance package for the site, for support people only (only applies when impersonating)`}
                      </Typography>

                      <AdvancedInputField
                        datePicker
                        boxedDatePicker
                        disablePast
                        hideBorder
                        containerClassName={styles.temporaryPackageExpiresAtContainer}
                        name="debugPackageExpiresAt"
                        label={t`Expires on`}
                        editable={editable}
                        hint={t`Default 14 days. Can be extended.`}
                        {...props}
                      />
                    </div>
                  </div>
                </form>
              );
            }}
          </Formik>
        </div>
        {(showAllPages || mySupportedPages["devices"]) && <div className={styles.siteButtonsContainer}>
          <Button
            className={styles.siteButtonsStyle}
            onClick={() => {
              history.push(`/devices`)
              setSelections({ customerId: customer, siteId: siteId, systemId: null, unitId: null });
            }}
          >
            {t`Site Devices`}
          </Button>
        </div>}
      </div>
    </ServiceNavigationBar>
  );
};

export default SiteDetails;
